// Mesmas regras do envio pela tela de Files (AB#87121): o servidor confere de novo pelos bytes.
export const ATTACHMENT_ACCEPT = ".pdf,.docx,.doc,.pptx,.txt,.md,.csv,.xlsx,.json,.png,.jpg,.jpeg";
export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

export function formatAttachmentSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Separa os arquivos aceitos dos que passam do limite, para avisar sem bloquear os outros. */
export function splitBySize(files: File[]): { accepted: File[]; tooLarge: File[] } {
  return {
    accepted: files.filter((f) => f.size <= MAX_ATTACHMENT_BYTES),
    tooLarge: files.filter((f) => f.size > MAX_ATTACHMENT_BYTES),
  };
}

type AttachmentState = {
  truncated?: boolean;
  readable?: boolean;
  summary_status?: "pending" | "ready" | "failed";
};

/**
 * O que o chip mostra (AB#87121, AC22/AC23). Um anexo que não coube na mensagem ganha um
 * resumo do arquivo inteiro em segundo plano: preparando, pronto ou falhou. Mensagens de
 * antes do resumo existir continuam como "parcial".
 */
export function attachmentBadge(
  a: AttachmentState,
): "unreadable" | "summarizing" | "summarized" | "summaryFailed" | "partial" | null {
  if (a.readable === false) return "unreadable";
  if (!a.truncated) return null;
  if (a.summary_status === "pending") return "summarizing";
  if (a.summary_status === "ready") return "summarized";
  if (a.summary_status === "failed") return "summaryFailed";
  return "partial";
}

type WithAttachments = { id: string; metadata?: { attachments?: AttachmentState[] } & Record<string, unknown> };

/** Há resumo em preparo em alguma mensagem da conversa aberta. */
export function hasPendingSummary(messages: WithAttachments[]): boolean {
  return messages.some((m) => m.metadata?.attachments?.some((a) => a.summary_status === "pending"));
}

/**
 * Troca só os anexos das mensagens que o servidor devolveu, sem mexer no resto da
 * conversa (uma resposta pode estar chegando pelo stream ao mesmo tempo).
 */
export function mergeAttachmentUpdates<T extends WithAttachments>(
  messages: T[],
  fresh: Map<string, AttachmentState[] | undefined>,
): T[] {
  let changed = false;
  const next = messages.map((m) => {
    const attachments = fresh.get(m.id);
    if (!attachments || !m.metadata?.attachments) return m;
    if (JSON.stringify(attachments) === JSON.stringify(m.metadata.attachments)) return m;
    changed = true;
    return { ...m, metadata: { ...m.metadata, attachments } };
  });
  return changed ? next : messages;
}
